import { SectionEyebrow } from "../components/CommonComponents";
import { ArrowUpRight } from "../components/CommonComponents";

const CourseEligibility = ({ course }) => {
  const items = [
    {
      number: "01",
      label: "Academic Requirement",
      title: course?.eligibility || "As per programme eligibility criteria",
      description:
        "Applicants must satisfy the prescribed academic eligibility requirements for admission to this programme.",
    },
    {
      number: "02",
      label: "Programme Admission",
      title: "University Admission Guidelines",
      description:
        "Admission is subject to fulfilment of the University's prescribed admission requirements and applicable programme guidelines.",
    },
    {
      number: "03",
      label: "Documentation",
      title: "Academic & Identity Documents",
      description:
        "Applicants should keep the required academic records, certificates, identity documents and other applicable documents ready during the admission process.",
    },
    {
      number: "04",
      label: "Final Verification",
      title: "Eligibility Verification",
      description:
        "Admission remains subject to verification of eligibility, submitted information and supporting documents by the University.",
    },
  ];

  return (
    <section
      id="eligibility"
      aria-labelledby="eligibility-heading"
      className="relative overflow-hidden bg-white py-20 lg:py-28"
    >
      {/* Background decoration */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-0 hidden h-full w-[22%] bg-[#F8F6F1] lg:block"
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-[7%] top-[90px] hidden h-[170px] w-[170px] rounded-full border border-gold/20 xl:block"
      />

      <div className="container relative z-[1] mx-auto">
        {/* ==================================================
            SECTION HEADER
        =================================================== */}
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
          <div>
            <SectionEyebrow>
              Admission Eligibility
            </SectionEyebrow>

            <span className="mt-8 block font-serif text-[72px] leading-none text-navy/[0.06] md:text-[96px]">
              Criteria 
            </span>
          </div>

          <div>
            <h2
              id="eligibility-heading"
              className="max-w-[850px] font-serif text-[clamp(42px,5vw,70px)] font-normal leading-[1.04] tracking-[-0.04em] text-navy"
            >
              Eligibility Criteria for{" "}
              <em className="font-normal italic text-burgundy">
                {course?.title || "This Programme"}
              </em>
            </h2>

            <p className="mt-7 max-w-[720px] text-lg leading-[1.8] text-ink/65">
              Review the academic eligibility and admission requirements before
              beginning your application to ISBM University.
            </p>
          </div>
        </div>

        {/* ==================================================
            PRIMARY ELIGIBILITY STATEMENT
        =================================================== */}
        <div className="relative mt-14 overflow-hidden border border-navy/15 bg-[#EEF4F8] lg:mt-16">
          <div className="grid lg:grid-cols-[0.32fr_1.68fr]">
            {/* Label */}
            <div className="flex flex-col justify-between border-b border-navy/15 bg-navy p-7 text-white lg:min-h-[270px] lg:border-b-0 lg:border-r lg:p-10">
              <div>
                <span className="text-sm font-bold uppercase tracking-[0.16em] text-gold">
                  Minimum Eligibility
                </span>
              </div>

              <div className="mt-12 lg:mt-0">
                <span className="font-serif text-[54px] leading-none text-white/10">
                  ✓
                </span>

                <p className="mt-3 text-sm leading-[1.6] text-white/55">
                  Academic requirement for programme admission
                </p>
              </div>
            </div>

            {/* Main eligibility */}
            <div className="relative flex min-h-[270px] items-center px-7 py-10 md:px-10 lg:px-14 lg:py-12">
              <div className="max-w-[900px]">
                <span className="text-sm font-bold uppercase tracking-[0.15em] text-burgundy">
                  You should have
                </span>

                <h3 className="mt-5 font-serif text-[clamp(28px,3vw,42px)] font-normal leading-[1.3] tracking-[-0.02em] text-navy">
                  {course?.eligibility ||
                    "Fulfilled the prescribed eligibility requirements for this programme."}
                </h3>

                <div className="mt-7 flex items-start gap-4 border-t border-navy/15 pt-5">
                  <span className="mt-2 h-2 w-2 shrink-0 rotate-45 bg-gold" />

                  <p className="max-w-[700px] text-base leading-[1.7] text-ink/60">
                    Applicants are advised to review the programme requirements
                    and admission guidelines before submitting an application.
                  </p>
                </div>
              </div>

              <span
                aria-hidden="true"
                className="pointer-events-none absolute -right-5 -top-8 hidden font-serif text-[150px] leading-none text-navy/[0.025] md:block"
              >
                E
              </span>
            </div>
          </div>
        </div>

        {/* ==================================================
            REQUIREMENTS + SIDE PANEL
        =================================================== */}
        <div className="mt-10 grid gap-10 lg:grid-cols-[1.45fr_0.55fr] lg:gap-0">
          {/* Requirement rows */}
          <div className="border border-navy/15 lg:border-r-0">
            <div className="flex items-center justify-between border-b border-navy/15 px-6 py-5 md:px-8">
              <span className="text-sm font-bold uppercase tracking-[0.15em] text-navy">
                Admission Requirements
              </span>

              <span className="text-sm font-semibold text-ink/40">
                04 checkpoints
              </span>
            </div>

            {items.map((item, index) => (
              <article
                key={item.number}
                className={`
                  group
                  relative
                  grid
                  gap-5
                  px-6
                  py-8
                  transition-colors
                  duration-300
                  hover:bg-[#FDF7FC]
                  md:grid-cols-[70px_180px_1fr]
                  md:gap-7
                  md:px-8
                  md:py-9

                  ${index !== items.length - 1
                    ? "border-b border-navy/15"
                    : ""
                  }
                `}
              >
                <span className="font-serif text-[27px] leading-none text-gold-dark">
                  {item.number}
                </span>

                <span className="text-sm font-bold uppercase leading-[1.6] tracking-[0.12em] text-burgundy">
                  {item.label}
                </span>

                <div>
                  <h3 className="font-serif text-[24px] font-normal leading-[1.3] text-navy">
                    {item.title}
                  </h3>

                  <p className="mt-3 max-w-[590px] text-base leading-[1.7] text-ink/60">
                    {item.description}
                  </p>
                </div>

                <span
                  aria-hidden="true"
                  className="absolute bottom-0 left-0 h-[2px] w-0 bg-burgundy transition-all duration-500 group-hover:w-full"
                />
              </article>
            ))}
          </div>

          {/* ================================================
              BEFORE YOU APPLY
          ================================================= */}
          <aside className="relative overflow-hidden bg-burgundy p-7 text-white md:p-9 lg:p-10">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-[90px] -top-[90px] h-[230px] w-[230px] rounded-full border border-white/10"
            />

            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-[40px] -top-[40px] h-[130px] w-[130px] rounded-full border border-gold/25"
            />

            <div className="relative z-[1] flex h-full flex-col">
              <span className="text-sm font-bold uppercase tracking-[0.16em] text-gold">
                Before You Apply
              </span>

              <h3 className="mt-5 font-serif text-[32px] font-normal leading-[1.2] md:text-[36px]">
                Check your eligibility with confidence.
              </h3>

              <p className="mt-5 text-base leading-[1.75] text-white/70">
                Not sure whether your academic background meets the programme
                requirements? Our admissions team can help you understand the
                eligibility and application process.
              </p>

              <div className="my-8 h-px w-full bg-white/15" />

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <span className="mt-1 grid h-6 w-6 shrink-0 place-items-center rounded-full border border-gold/50 text-xs text-gold">
                    ✓
                  </span>

                  <span className="text-base leading-[1.55] text-white/80">
                    Review academic eligibility
                  </span>
                </div>

                <div className="flex items-start gap-4">
                  <span className="mt-1 grid h-6 w-6 shrink-0 place-items-center rounded-full border border-gold/50 text-xs text-gold">
                    ✓
                  </span>

                  <span className="text-base leading-[1.55] text-white/80">
                    Prepare required documents
                  </span>
                </div>

                <div className="flex items-start gap-4">
                  <span className="mt-1 grid h-6 w-6 shrink-0 place-items-center rounded-full border border-gold/50 text-xs text-gold">
                    ✓
                  </span>

                  <span className="text-base leading-[1.55] text-white/80">
                    Connect with admissions support
                  </span>
                </div>
              </div>

              <div className="mt-auto pt-10">
                <a
                  href="/apply-online"
                  className="group flex min-h-[56px] w-full items-center justify-between bg-white px-6 text-sm font-bold text-navy transition-colors duration-300 hover:bg-gold"
                >
                  Apply for Admission

                  <ArrowUpRight className="h-[18px] w-[18px] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>

                <a
                  href="/contact-us"
                  className="group mt-3 flex min-h-[54px] w-full items-center justify-between border border-white/25 px-6 text-sm font-bold text-white transition-colors duration-300 hover:border-gold hover:text-gold"
                >
                  Talk to Admissions

                  <span className="transition-transform duration-300 group-hover:translate-x-1">
                    →
                  </span>
                </a>
              </div>
            </div>
          </aside>
        </div>

        {/* ==================================================
            DISCLAIMER
        =================================================== */}
        <div className="mt-7 flex flex-col gap-4 border-t border-navy/15 pt-6 md:flex-row md:items-start">
          <span className="shrink-0 text-sm font-bold uppercase tracking-[0.13em] text-burgundy">
            Please Note
          </span>

          <span className="hidden h-5 w-px bg-navy/20 md:block" />

          <p className="max-w-[950px] text-sm leading-[1.7] text-ink/55">
            Eligibility and admission requirements may vary according to the
            programme, applicant category and applicable University guidelines.
            Applicants should confirm the current requirements before applying.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CourseEligibility